import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vModelText as _vModelText, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between" }
const _hoisted_2 = {
  key: 0,
  class: "flex-1 max-w-md"
}
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = { class: "flex items-center space-x-2" }
const _hoisted_6 = {
  key: 0,
  class: "flex flex-wrap gap-2"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "overflow-x-auto" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "flex items-center space-x-1" }
const _hoisted_11 = {
  key: 0,
  class: "flex flex-col"
}
const _hoisted_12 = ["data-type"]
const _hoisted_13 = {
  key: 0,
  class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right table-cell-border"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = ["colspan"]
const _hoisted_16 = { class: "flex-1 flex justify-between sm:hidden" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" }
const _hoisted_20 = { class: "font-medium" }
const _hoisted_21 = { class: "font-medium" }
const _hoisted_22 = { class: "font-medium" }
const _hoisted_23 = { class: "relative z-0 inline-flex rounded-md shadow-sm -space-x-px" }
const _hoisted_24 = ["disabled"]
const _hoisted_25 = ["disabled"]
const _hoisted_26 = ["onClick"]
const _hoisted_27 = ["disabled"]
const _hoisted_28 = ["disabled"]

import { ref, computed, watch, useSlots, inject } from 'vue'
import { useTheme } from '../utils/useTheme'

// Use theme via inject with a fallback
interface Column {
  field: string
  label: string
  sortable?: boolean
  formatter?: (value: any, row: any) => string
  class?: string | string[]
  searchable?: boolean
  filterable?: boolean
  type?: 'text' | 'number' | 'date' | 'boolean'
}

interface TableConfig {
  enableSearch?: boolean
  enableSort?: boolean
  enableFilter?: boolean
  searchPlaceholder?: string
  idField?: string
  emptyMessage?: string
  enablePagination?: boolean
  itemsPerPage?: number
  defaultSort?: {
    field: string
    order: 'asc' | 'desc'
  }
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    data: {},
    columns: {},
    config: {}
  },
  setup(__props: any, { expose: __expose }) {

const theme = inject('theme', useTheme())

const props = __props

const slots = useSlots()

const defaultConfig: TableConfig = {
  enableSearch: true,
  enableSort: true,
  enableFilter: true,
  searchPlaceholder: 'Search all fields...',
  idField: '_id',
  emptyMessage: 'No data available',
  enablePagination: true,
  itemsPerPage: 10
}

const config = computed(() => ({ ...defaultConfig, ...props.config }))

// Search
const searchQuery = ref('')

// Sorting
const sortField = ref(config.value.defaultSort?.field || '')
const sortOrder = ref<'asc' | 'desc'>(config.value.defaultSort?.order || 'asc')

// Filtering
const activeFilters = ref<Record<string, any>>({})

// Pagination
const currentPage = ref(1)
const itemsPerPage = computed(() => config.value.itemsPerPage || 10)

// Helper functions
const getItemKey = (item: any): string => {
  const idField = config.value.idField || '_id'
  return item[idField]?.toString() || Math.random().toString()
}

const getColSpan = computed(() => {
  return props.columns.length + (slots['row-actions'] ? 1 : 0)
})

// Add deep search helper function
const deepSearch = (obj: any, searchText: string): boolean => {
  if (!obj) return false

  // Convert to lower case for case-insensitive search
  searchText = searchText.toLowerCase()

  // If it's a string, check if it contains the search text
  if (typeof obj === 'string') {
    return obj.toLowerCase().includes(searchText)
  }

  // If it's a number or boolean, convert to string and check
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj).toLowerCase().includes(searchText)
  }

  // If it's an array, search through all elements
  if (Array.isArray(obj)) {
    return obj.some((item) => deepSearch(item, searchText))
  }

  // If it's an object, search through all values
  if (typeof obj === 'object') {
    return Object.values(obj).some((value) => deepSearch(value, searchText))
  }

  return false
}

// Update the filteredData computed property
const filteredData = computed(() => {
  let result = [...props.data]

  // Apply search
  if (searchQuery.value && config.value.enableSearch) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((item) => {
      // If specific searchable columns are defined, only search those
      if (props.columns.some((col) => col.searchable === true)) {
        return props.columns.some((column) => {
          if (column.searchable === false) return false
          const value = item[column.field]
          return deepSearch(value, query)
        })
      }
      // Otherwise, perform deep search on the entire item
      return deepSearch(item, query)
    })
  }

  // Apply filters
  Object.entries(activeFilters.value).forEach(([field, value]) => {
    result = result.filter((item) => String(item[field]) === String(value))
  })

  // Apply sorting
  if (sortField.value && config.value.enableSort) {
    result.sort((a, b) => {
      const aVal = a[sortField.value]
      const bVal = b[sortField.value]
      if (aVal === bVal) return 0
      const comparison = aVal > bVal ? 1 : -1
      return sortOrder.value === 'asc' ? comparison : -comparison
    })
  }

  return result
})

// Pagination computed properties
const totalPages = computed(() => Math.ceil(filteredData.value.length / itemsPerPage.value))
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value)
const endIndex = computed(() => startIndex.value + itemsPerPage.value)
const processedData = computed(() => {
  if (!config.value.enablePagination) return filteredData.value
  return filteredData.value.slice(startIndex.value, endIndex.value)
})

const displayedPages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const range: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) range.push(i)
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 5; i++) range.push(i)
      range.push('...', total)
    } else if (current >= total - 2) {
      range.push(1, '...')
      for (let i = total - 4; i <= total; i++) range.push(i)
    } else {
      range.push(1, '...')
      for (let i = current - 1; i <= current + 1; i++) range.push(i)
      range.push('...', total)
    }
  }

  return range
})

const hasActiveFilters = computed(() => Object.keys(activeFilters.value).length > 0)

// Methods
const handleSort = (field: string) => {
  if (!config.value.enableSort) return
  const column = props.columns.find((col) => col.field === field)
  if (column?.sortable === false) return

  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

const getColumnLabel = (field: string) => {
  const column = props.columns.find((col) => col.field === field)
  return column?.label || field
}

const removeFilter = (field: string) => {
  const newFilters = { ...activeFilters.value }
  delete newFilters[field]
  activeFilters.value = newFilters
}

// Watch for changes in search/filter to reset pagination
watch([searchQuery, activeFilters], () => {
  currentPage.value = 1
})

// Expose methods and properties that might be needed by parent
__expose({
  currentPage,
  totalPages,
  activeFilters,
  searchQuery,
  sortField,
  sortOrder,
  resetFilters: () => {
    activeFilters.value = {}
    searchQuery.value = ''
    currentPage.value = 1
    sortField.value = config.value.defaultSort?.field || ''
    sortOrder.value = config.value.defaultSort?.order || 'asc'
  }
})

const getColumnType = (column: Column, value: any): string => {
  if (column.type) return column.type
  if (typeof value === 'number') return 'number'
  if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value))))
    return 'date'
  if (typeof value === 'boolean') return 'boolean'
  return 'text'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100',
      'rounded-xl shadow-lg border'
    ])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'px-4 py-3 border-b space-y-3'
      ])
    }, [
      _createElementVNode("div", _hoisted_1, [
        (config.value.enableSearch)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                  type: "text",
                  class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'border-gray-300 text-gray-700',
                'w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
              ]),
                  placeholder: config.value.searchPlaceholder || 'Search...'
                }, null, 10, _hoisted_4), [
                  [_vModelText, searchQuery.value]
                ]),
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" }, [
                  _createElementVNode("svg", {
                    class: "h-5 w-5 text-gray-400",
                    fill: "none",
                    stroke: "currentColor",
                    viewBox: "0 0 24 24"
                  }, [
                    _createElementVNode("path", {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      strokeWidth: "2",
                      d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    })
                  ])
                ], -1))
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _renderSlot(_ctx.$slots, "table-actions")
        ])
      ]),
      (hasActiveFilters.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (value, field) => {
              return (_openBlock(), _createElementBlock("div", {
                key: field,
                class: _normalizeClass([
            _unref(theme).isDark.value ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-700',
            'inline-flex items-center px-2 py-1 rounded-md text-sm'
          ])
              }, [
                _createTextVNode(_toDisplayString(getColumnLabel(field)) + ": " + _toDisplayString(value) + " ", 1),
                _createElementVNode("button", {
                  class: "ml-1 text-blue-500 hover:text-blue-700",
                  onClick: ($event: any) => (removeFilter(field))
                }, " × ", 8, _hoisted_7)
              ], 2))
            }), 128))
          ]))
        : _createCommentVNode("", true)
    ], 2),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("table", {
        class: _normalizeClass(["min-w-full divide-y", [_unref(theme).isDark.value ? 'divide-gray-700' : 'divide-gray-200']])
      }, [
        _createElementVNode("thead", {
          class: _normalizeClass([_unref(theme).isDark.value ? 'bg-gray-900' : 'bg-gray-50'])
        }, [
          _createElementVNode("tr", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
              return (_openBlock(), _createElementBlock("th", {
                key: column.field,
                class: _normalizeClass(["px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none table-cell-border", [
                _unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-500',
                sortField.value === column.field ? 'text-blue-600' : ''
              ]]),
                onClick: ($event: any) => (handleSort(column.field))
              }, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("span", null, _toDisplayString(column.label), 1),
                  (column.sortable !== false)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                        (_openBlock(), _createElementBlock("svg", {
                          class: _normalizeClass([
                      'w-3 h-3 transform',
                      sortField.value === column.field && sortOrder.value === 'asc'
                        ? 'text-blue-600'
                        : 'text-gray-400'
                    ]),
                          fill: "none",
                          stroke: "currentColor",
                          viewBox: "0 0 24 24"
                        }, _cache[8] || (_cache[8] = [
                          _createElementVNode("path", {
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: "2",
                            d: "M5 15l7-7 7 7"
                          }, null, -1)
                        ]), 2)),
                        (_openBlock(), _createElementBlock("svg", {
                          class: _normalizeClass([
                      'w-3 h-3 transform',
                      sortField.value === column.field && sortOrder.value === 'desc'
                        ? 'text-blue-600'
                        : 'text-gray-400'
                    ]),
                          fill: "none",
                          stroke: "currentColor",
                          viewBox: "0 0 24 24"
                        }, _cache[9] || (_cache[9] = [
                          _createElementVNode("path", {
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: "2",
                            d: "M19 9l-7 7-7-7"
                          }, null, -1)
                        ]), 2))
                      ]))
                    : _createCommentVNode("", true)
                ])
              ], 10, _hoisted_9))
            }), 128)),
            (_ctx.$slots['row-actions'])
              ? (_openBlock(), _createElementBlock("th", {
                  key: 0,
                  class: _normalizeClass(["px-6 py-3 text-right text-xs font-medium uppercase tracking-wider table-cell-border", [_unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-500']])
                }, " Actions ", 2))
              : _createCommentVNode("", true)
          ])
        ], 2),
        _createElementVNode("tbody", {
          class: _normalizeClass([_unref(theme).isDark.value ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'])
        }, [
          (processedData.value.length)
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(processedData.value, (item) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: getItemKey(item),
                  class: _normalizeClass([_unref(theme).isDark.value ? 'hover:bg-gray-700' : 'hover:bg-gray-50'])
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: column.field,
                      class: _normalizeClass(["px-6 py-4 whitespace-nowrap text-sm table-cell-border", [_unref(theme).isDark.value ? 'text-gray-200' : 'text-gray-800', column.class]]),
                      "data-type": getColumnType(column, item[column.field])
                    }, [
                      (_ctx.$slots[column.field])
                        ? _renderSlot(_ctx.$slots, column.field, {
                            key: 0,
                            item: item,
                            value: item[column.field]
                          })
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            (column.formatter)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(_toDisplayString(column.formatter(item[column.field], item)), 1)
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(_toDisplayString(item[column.field]), 1)
                                ], 64))
                          ], 64))
                    ], 10, _hoisted_12))
                  }), 128)),
                  (_ctx.$slots['row-actions'])
                    ? (_openBlock(), _createElementBlock("td", _hoisted_13, [
                        _renderSlot(_ctx.$slots, "row-actions", { item: item })
                      ]))
                    : _createCommentVNode("", true)
                ], 2))
              }), 128))
            : (_openBlock(), _createElementBlock("tr", _hoisted_14, [
                _createElementVNode("td", {
                  colspan: getColSpan.value,
                  class: _normalizeClass(["px-6 py-4 text-center", [_unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-500']])
                }, _toDisplayString(config.value.emptyMessage || 'No data available'), 11, _hoisted_15)
              ]))
        ], 2)
      ], 2)
    ]),
    (config.value.enablePagination && totalPages.value > 1)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass([
        _unref(theme).isDark.value ? 'border-gray-700' : 'border-gray-200',
        'px-4 py-3 border-t sm:px-6 flex items-center justify-between'
      ])
        }, [
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("button", {
              disabled: currentPage.value === 1,
              class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
            'relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
          ]),
              onClick: _cache[1] || (_cache[1] = ($event: any) => (currentPage.value--))
            }, " Previous ", 10, _hoisted_17),
            _createElementVNode("button", {
              disabled: currentPage.value === totalPages.value,
              class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
            'ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
          ]),
              onClick: _cache[2] || (_cache[2] = ($event: any) => (currentPage.value++))
            }, " Next ", 10, _hoisted_18)
          ]),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-700', 'text-sm'])
              }, [
                _cache[10] || (_cache[10] = _createTextVNode(" Showing ")),
                _createElementVNode("span", _hoisted_20, _toDisplayString(startIndex.value + 1), 1),
                _cache[11] || (_cache[11] = _createTextVNode(" to ")),
                _createElementVNode("span", _hoisted_21, _toDisplayString(Math.min(endIndex.value, filteredData.value.length)), 1),
                _cache[12] || (_cache[12] = _createTextVNode(" of ")),
                _createElementVNode("span", _hoisted_22, _toDisplayString(filteredData.value.length), 1),
                _cache[13] || (_cache[13] = _createTextVNode(" results "))
              ], 2)
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("nav", _hoisted_23, [
                _createElementVNode("button", {
                  disabled: currentPage.value === 1,
                  class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium'
              ]),
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (currentPage.value = 1))
                }, _cache[14] || (_cache[14] = [
                  _createElementVNode("span", { class: "sr-only" }, "First", -1),
                  _createTextVNode(" ⟪ ")
                ]), 10, _hoisted_24),
                _createElementVNode("button", {
                  disabled: currentPage.value === 1,
                  class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
              ]),
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (currentPage.value--))
                }, _cache[15] || (_cache[15] = [
                  _createElementVNode("span", { class: "sr-only" }, "Previous", -1),
                  _createTextVNode(" ⟨ ")
                ]), 10, _hoisted_25),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedPages.value, (page) => {
                  return (_openBlock(), _createElementBlock(_Fragment, { key: page }, [
                    (page !== '...')
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: _normalizeClass([
                  currentPage.value === page
                    ? _unref(theme).isDark.value
                      ? 'z-10 bg-blue-900 border-blue-600 text-blue-300'
                      : 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : _unref(theme).isDark.value
                      ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ]),
                          onClick: ($event: any) => (currentPage.value = Number(page))
                        }, _toDisplayString(page), 11, _hoisted_26))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 1,
                          class: _normalizeClass([
                  _unref(theme).isDark.value
                    ? 'border-gray-600 bg-gray-700 text-gray-400'
                    : 'border-gray-300 bg-white text-gray-700',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ])
                        }, " ... ", 2))
                  ], 64))
                }), 128)),
                _createElementVNode("button", {
                  disabled: currentPage.value === totalPages.value,
                  class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
              ]),
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (currentPage.value++))
                }, _cache[16] || (_cache[16] = [
                  _createElementVNode("span", { class: "sr-only" }, "Next", -1),
                  _createTextVNode(" ⟩ ")
                ]), 10, _hoisted_27),
                _createElementVNode("button", {
                  disabled: currentPage.value === totalPages.value,
                  class: _normalizeClass([
                _unref(theme).isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                'relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium'
              ]),
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (currentPage.value = totalPages.value))
                }, _cache[17] || (_cache[17] = [
                  _createElementVNode("span", { class: "sr-only" }, "Last", -1),
                  _createTextVNode(" ⟫ ")
                ]), 10, _hoisted_28)
              ])
            ])
          ])
        ], 2))
      : _createCommentVNode("", true)
  ], 2))
}
}

})